import { notifications } from '@mantine/notifications'
import { useQueryClient } from '@tanstack/react-query'

import { INVALIDATE_KEYS } from '@/constants/invalidateGoalQueryKeys.constants'

import {
  useGoalsServiceGoalsControllerAddStep,
  useGoalsServiceGoalsControllerArchive,
  useGoalsServiceGoalsControllerComplete,
  useGoalsServiceGoalsControllerDeleteStep,
  useGoalsServiceGoalsControllerReactivate,
  useGoalsServiceGoalsControllerReorderSteps,
  useGoalsServiceGoalsControllerToggleStep,
  useGoalsServiceGoalsControllerUpdateStep,
} from '@/__generated__/queries'

function useInvalidateGoalQueries() {
  const queryClient = useQueryClient()

  return () => {
    INVALIDATE_KEYS.forEach(key =>
      queryClient.invalidateQueries({ queryKey: [key] }),
    )
  }
}

export function useCompleteGoal(onSuccess?: () => void) {
  const invalidate = useInvalidateGoalQueries()

  return useGoalsServiceGoalsControllerComplete({
    onSuccess: () => {
      notifications.show({
        color: 'green',
        message: 'Goal has been marked as completed',
        title: 'Goal completed',
      })
      invalidate()
      onSuccess?.()
    },
  })
}

export function useArchiveGoal(onSuccess?: () => void) {
  const invalidate = useInvalidateGoalQueries()

  return useGoalsServiceGoalsControllerArchive({
    onSuccess: () => {
      notifications.show({
        color: 'green',
        message: 'Goal has been archived',
        title: 'Goal archived',
      })
      invalidate()
      onSuccess?.()
    },
  })
}

export function useReactivateGoal(onSuccess?: () => void) {
  const invalidate = useInvalidateGoalQueries()

  return useGoalsServiceGoalsControllerReactivate({
    onSuccess: () => {
      notifications.show({
        color: 'green',
        message: 'Goal has been reactivated',
        title: 'Goal reactivated',
      })
      invalidate()
      onSuccess?.()
    },
  })
}

export function useAddGoalStep(onSuccess?: () => void) {
  const invalidate = useInvalidateGoalQueries()

  return useGoalsServiceGoalsControllerAddStep({
    onSuccess: () => {
      notifications.show({
        color: 'green',
        message: 'Step added successfully',
        title: 'Step added',
      })
      invalidate()
      onSuccess?.()
    },
  })
}

export function useUpdateGoalStep(onSuccess?: () => void) {
  const invalidate = useInvalidateGoalQueries()

  return useGoalsServiceGoalsControllerUpdateStep({
    onSuccess: () => {
      notifications.show({
        color: 'green',
        message: 'Step updated successfully',
        title: 'Step updated',
      })
      invalidate()
      onSuccess?.()
    },
  })
}

export function useDeleteGoalStep(onSuccess?: () => void) {
  const invalidate = useInvalidateGoalQueries()

  return useGoalsServiceGoalsControllerDeleteStep({
    onSuccess: () => {
      notifications.show({
        color: 'green',
        message: 'Step deleted successfully',
        title: 'Step deleted',
      })
      invalidate()
      onSuccess?.()
    },
  })
}

export function useToggleGoalStep() {
  const invalidate = useInvalidateGoalQueries()

  return useGoalsServiceGoalsControllerToggleStep({
    onSuccess: () => {
      invalidate()
    },
  })
}

export function useReorderGoalSteps() {
  const invalidate = useInvalidateGoalQueries()

  return useGoalsServiceGoalsControllerReorderSteps({
    onSuccess: () => {
      invalidate()
    },
  })
}
